import { createBrowserRouter, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { ProtectedRoute } from '@/shared/components/ProtectedRoute'
import { WorkspaceGuard } from '@/shared/components/WorkspaceGuard'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'
import { useTimerSync } from '@/features/timer/hooks/useTimerSync'
import AppLayout from '@/shared/components/AppLayout'

// Lazy load page components for code splitting
const TimeEntriesPage = lazy(() => import('@/features/time-entries/components/TimeEntriesPage').then(m => ({ default: m.TimeEntriesPage })))
const DashboardPage = lazy(() => import('@/features/dashboard/components/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ReportsPage = lazy(() => import('@/features/reports/components/ReportsPage').then(m => ({ default: m.ReportsPage })))
const ProjectsPage = lazy(() => import('@/features/projects/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const ClientsPage = lazy(() => import('@/features/clients/pages/ClientsPage').then(m => ({ default: m.ClientsPage })))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage').then(m => ({ default: m.SignupPage })))
const VerifyEmailPage = lazy(() => import('@/features/auth/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })))
const PasswordResetRequestPage = lazy(() => import('@/features/auth/pages/PasswordResetRequestPage').then(m => ({ default: m.PasswordResetRequestPage })))
const PasswordResetPage = lazy(() => import('@/features/auth/pages/PasswordResetPage').then(m => ({ default: m.PasswordResetPage })))
const AcceptInvitationPage = lazy(() => import('@/features/workspaces/pages/AcceptInvitationPage').then(m => ({ default: m.AcceptInvitationPage })))
const WorkspaceList = lazy(() => import('@/features/workspaces/components/WorkspaceList').then(m => ({ default: m.WorkspaceList })))
const WorkspaceSettingsPage = lazy(() => import('@/features/workspaces/pages/WorkspaceSettingsPage').then(m => ({ default: m.WorkspaceSettingsPage })))
const MembersPage = lazy(() => import('@/features/workspaces/pages/MembersPage').then(m => ({ default: m.MembersPage })))
const AdminPage = lazy(() => import('@/features/admin/components/AdminPage').then(m => ({ default: m.AdminPage })))
const AdminRouteGuard = lazy(() => import('@/features/admin/components/AdminRouteGuard').then(m => ({ default: m.AdminRouteGuard })))
const JiraIntegrationPage = lazy(() => import('@/features/jira/pages/JiraIntegrationPage').then(m => ({ default: m.JiraIntegrationPage })))
const SettingsPage = lazy(() => import('@/features/settings/components/SettingsPage').then(m => ({ default: m.SettingsPage })))
const TeamLayout = lazy(() => import('@/features/team/components/TeamLayout').then(m => ({ default: m.TeamLayout })))
const TeamPage = lazy(() => import('@/features/team/components/TeamPage').then(m => ({ default: m.TeamPage })))
const TeamMembersPage = lazy(() => import('@/features/team/components/TeamMembersPage').then(m => ({ default: m.TeamMembersPage })))
const TeamsPage = lazy(() => import('@/features/team/components/TeamsPage').then(m => ({ default: m.TeamsPage })))
const MemberDetailsPage = lazy(() => import('@/features/team/components/MemberDetailsPage').then(m => ({ default: m.MemberDetailsPage })))
const ProjectAllocationView = lazy(() => import('@/features/team/components/ProjectAllocationView').then(m => ({ default: m.ProjectAllocationView })))
const OidcCallbackPage = lazy(() => import('@/features/auth/pages/OidcCallbackPage').then(m => ({ default: m.OidcCallbackPage })))
const OidcProvidersPage = lazy(() => import('@/features/admin/components/OidcProvidersPage').then(m => ({ default: m.OidcProvidersPage })))
const WorkspaceTimeSettingsPage = lazy(() => import('@/features/workspaces/pages/WorkspaceTimeSettingsPage').then(m => ({ default: m.WorkspaceTimeSettingsPage })))
const TeamsPageWorkspace = lazy(() => import('@/features/team/workspace-teams/pages/TeamsPage').then(m => ({ default: m.TeamsPage })))
const ApiKeysPage = lazy(() => import('@/features/api-keys/pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })))

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
  </div>
)

// Root layout: initialises app-wide side effects that need to run once after auth
function RootLayout() {
  useTimerSync()
  return <Outlet />
}

// Wrapper component for MembersPage to get workspaceId from store
function MembersPageWrapper() {
  const { selectedWorkspace } = useWorkspaceStore()
  const navigate = useNavigate()

  if (!selectedWorkspace) {
    return <Navigate to="/workspaces" replace />
  }

  return (
    <MembersPage
      workspaceId={selectedWorkspace.id}
      onBack={() => navigate('/settings')}
    />
  )
}

// Wrapper with Suspense for lazy-loaded components
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
)

export const router = createBrowserRouter([
  {
    // Single root element: runs useTimerSync once for the whole app
    element: <RootLayout />,
    children: [
      // ── Public / unauthenticated routes ──────────────────────────────────
      {
        path: '/login',
        element: <LazyWrapper><LoginPage /></LazyWrapper>,
      },
      {
        path: '/signup',
        element: <LazyWrapper><SignupPage /></LazyWrapper>,
      },
      {
        path: '/verify-email',
        element: <LazyWrapper><VerifyEmailPage /></LazyWrapper>,
      },
      {
        path: '/password-reset-request',
        element: <LazyWrapper><PasswordResetRequestPage /></LazyWrapper>,
      },
      {
        path: '/password-reset',
        element: <LazyWrapper><PasswordResetPage /></LazyWrapper>,
      },
      {
        path: '/auth/callback',
        element: <LazyWrapper><OidcCallbackPage /></LazyWrapper>,
      },
      {
        path: '/invitations/accept',
        element: <LazyWrapper><AcceptInvitationPage /></LazyWrapper>,
      },

      // ── Authenticated – workspace picker & settings (no WorkspaceGuard) ──
      {
        path: '/workspaces',
        element: (
          <ProtectedRoute>
            <AppLayout>
              <LazyWrapper><WorkspaceList /></LazyWrapper>
            </AppLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/workspaces/:id',
        element: <Navigate to="settings" replace />,
      },
      {
        path: '/workspaces/:id/settings',
        element: (
          <ProtectedRoute>
            <AppLayout>
              <LazyWrapper><WorkspaceSettingsPage /></LazyWrapper>
            </AppLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/workspaces/:id/teams',
        element: (
          <ProtectedRoute>
            <AppLayout>
              <LazyWrapper><TeamsPageWorkspace /></LazyWrapper>
            </AppLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/workspaces/:id/api-keys',
        element: (
          <ProtectedRoute>
            <AppLayout>
              <LazyWrapper><ApiKeysPage /></LazyWrapper>
            </AppLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <AppLayout>
              <LazyWrapper><SettingsPage /></LazyWrapper>
            </AppLayout>
          </ProtectedRoute>
        ),
      },

      // ── Authenticated – workspace-scoped routes (WorkspaceGuard required) ─
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <WorkspaceGuard>
              <AppLayout>
                <Outlet />
              </AppLayout>
            </WorkspaceGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <LazyWrapper><DashboardPage /></LazyWrapper>,
          },
          {
            path: 'time-entries',
            element: <LazyWrapper><TimeEntriesPage /></LazyWrapper>,
          },
          {
            path: 'projects',
            element: <LazyWrapper><ProjectsPage /></LazyWrapper>,
          },
          {
            path: 'clients',
            element: <LazyWrapper><ClientsPage /></LazyWrapper>,
          },
          {
            path: 'reports',
            element: <LazyWrapper><ReportsPage /></LazyWrapper>,
          },
          {
            path: 'team',
            element: <LazyWrapper><TeamLayout /></LazyWrapper>,
            children: [
              {
                index: true,
                element: <LazyWrapper><TeamPage /></LazyWrapper>,
              },
              {
                path: 'members',
                element: <LazyWrapper><TeamMembersPage /></LazyWrapper>,
              },
              {
                path: 'members/:memberId',
                element: <LazyWrapper><MemberDetailsPage /></LazyWrapper>,
              },
              {
                path: 'teams',
                element: <LazyWrapper><TeamsPage /></LazyWrapper>,
              },
              {
                path: 'projects',
                element: <LazyWrapper><ProjectAllocationView /></LazyWrapper>,
              },
            ],
          },
          {
            path: 'members',
            element: <LazyWrapper><MembersPageWrapper /></LazyWrapper>,
          },
          {
            path: 'admin',
            element: (
              <LazyWrapper>
                <AdminRouteGuard>
                  <AdminPage />
                </AdminRouteGuard>
              </LazyWrapper>
            ),
          },
          {
            path: 'integrations',
            element: (
              <LazyWrapper>
                <AdminRouteGuard>
                  <JiraIntegrationPage />
                </AdminRouteGuard>
              </LazyWrapper>
            ),
          },
          {
            path: 'workspace/settings',
            element: <LazyWrapper><WorkspaceTimeSettingsPage /></LazyWrapper>,
          },
          {
            path: 'oidc-providers',
            element: (
              <LazyWrapper>
                <AdminRouteGuard>
                  <OidcProvidersPage />
                </AdminRouteGuard>
              </LazyWrapper>
            ),
          },
        ],
      },

      // ── Catch-all ─────────────────────────────────────────────────────────
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

export default router
